import React, { useState, useEffect } from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Badge from 'react-bootstrap/Badge';
import AdminService from "../../../services/admin.service";
import PurchaseOrderService from "../../../services/purchaseorder.service";
import SubsiteService from "../../../services/subsite.service";
import MSMPagination from '../../MSMPagination';
import { Container, Row, Col } from 'react-bootstrap';
import { Search, XCircle } from 'react-bootstrap-icons';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const PER_PAGE = 25;

const formatDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-us', { year: "numeric", month: "short", day: "numeric" });
};

const AdminClosedPOList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useSelector(state => state.auth);
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sanmarFilter, setSanmarFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [subsiteFilter, setSubsiteFilter] = useState("all");
  const [subsites, setSubsites] = useState([]);
  const [resetPagination, setResetPagination] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return;
    SubsiteService.getAllSubsites().then(res => {
      setSubsites(res.data || []);
    }).catch(() => setSubsites([]));
  }, [isSuperAdmin]);

  const loadPOs = async (page, perPage, search, sanmar, source, subsiteId) => {
    setLoading(true);
    try {
      const response = await PurchaseOrderService.getClosedPurchaseOrders(
        page, perPage, search, sanmar, source, isSuperAdmin ? subsiteId : 'all'
      );
      setOrders(response.data || []);
      setTotalCount(parseInt(response.count, 10) || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPOs(1, PER_PAGE, searchTerm, sanmarFilter, sourceFilter, subsiteFilter);
    setResetPagination(prev => !prev);
  }, [searchTerm, sanmarFilter, sourceFilter, subsiteFilter, isSuperAdmin]);

  const reloadData = (range) => {
    loadPOs(range[0], range[1], searchTerm, sanmarFilter, sourceFilter, subsiteFilter);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleSanmarFilterChange = (event) => {
    setSanmarFilter(event.target.value);
  };

  const handleSourceFilterChange = (event) => {
    setSourceFilter(event.target.value);
  };

  const handleSubsiteFilterChange = (event) => {
    setSubsiteFilter(event.target.value);
  };

  const downloadPO = async (id) => {
    await AdminService.downloadPO(id);
  };

  const isUnsubmitted = (order) => {
    const needsSanmar = order.hasSanmar && !order.sentToSanmarDate;
    const needsSands = order.hasSands && !order.sentToSandsDate;
    return needsSanmar || needsSands;
  };

  return (
    <>
      <h1>Closed Purchase Orders</h1>
      <p className="text-muted">
        Unsubmitted POs are listed first. Showing page size of {PER_PAGE}.
      </p>

      <Container className="mb-3">
        <Row className="align-items-end g-3">
          {isSuperAdmin && (
            <Col md={3}>
              <Form.Label>Subsite</Form.Label>
              <Form.Select value={subsiteFilter} onChange={handleSubsiteFilterChange}>
                <option value="all">All Subsites</option>
                {subsites.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.subsiteName}{s.abbreviation ? ` (${s.abbreviation})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}
          <Col md={isSuperAdmin ? 3 : 4}>
            <Form onSubmit={handleSearchSubmit}>
              <Form.Label>Search by PO Number</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="PO number or code"
                  value={searchInput}
                  onChange={handleSearchChange}
                />
                <Button variant="primary" type="submit">
                  <Search /> Search
                </Button>
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={handleClearSearch}>
                    <XCircle /> Clear
                  </Button>
                )}
              </InputGroup>
            </Form>
          </Col>
          <Col md={2}>
            <Form.Label>Source Type</Form.Label>
            <Form.Select value={sourceFilter} onChange={handleSourceFilterChange}>
              <option value="all">All Sources</option>
              <option value="print">Print</option>
              <option value="sanmar">SanMar</option>
              <option value="sands">S&amp;S</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Submission Status</Form.Label>
            <Form.Select value={sanmarFilter} onChange={handleSanmarFilterChange}>
              <option value="all">All</option>
              <option value="notsubmitted">Not Submitted</option>
              <option value="submitted">Submitted (Sanmar or S&amp;S)</option>
            </Form.Select>
          </Col>
          <Col md={2} className="text-muted">
            {totalCount} total
          </Col>
        </Row>
      </Container>

      <MSMPagination reload={reloadData} perPage={PER_PAGE} total={totalCount} reset={resetPagination} />

      <Container fluid className="px-0">
        <Row
          className="align-items-center gx-2 py-3"
          style={{ border: "1px solid #CCCCCC", fontWeight: "bold", backgroundColor: "#f8f9fa", minHeight: 56 }}
        >
          <Col xs={1}>PO ID</Col>
          {isSuperAdmin && <Col xs={2}>Subsite</Col>}
          <Col xs={2}>PO Date</Col>
          <Col xs={isSuperAdmin ? 1 : 2}>Type</Col>
          <Col xs={isSuperAdmin ? 4 : 5}>Submission</Col>
          <Col xs={2}>Actions</Col>
        </Row>

        {loading && (
          <Row style={{ border: "1px solid #CCCCCC" }}>
            <Col className="py-3">Loading…</Col>
          </Row>
        )}

        {!loading && orders.map(order => (
          <Row
            key={order.id}
            className="align-items-center gx-2 py-3 flex-nowrap"
            style={{
              border: "1px solid #CCCCCC",
              borderTop: "none",
              minHeight: 72,
              backgroundColor: isUnsubmitted(order) ? "#fff8e1" : undefined
            }}
          >
            <Col xs={1}>{order.id}</Col>
            {isSuperAdmin && (
              <Col xs={2}>
                {order.subsite?.subsiteName || '—'}
              </Col>
            )}
            <Col xs={2}>{formatDate(order.createdAt) || ''}</Col>
            <Col xs={isSuperAdmin ? 1 : 2}>
              {order.isPrintPO ? <Badge bg="success">Print</Badge> : <Badge bg="secondary">Apparel</Badge>}
            </Col>
            <Col xs={isSuperAdmin ? 4 : 5}>
              {order.hasPrint && !order.hasSanmar && !order.hasSands ? (
                <em>N/A (Print PO)</em>
              ) : (
                <>
                  {order.hasSanmar && (
                    <div>
                      Sanmar:{" "}
                      {order.sentToSanmarDate
                        ? formatDate(order.sentToSanmarDate)
                        : <Badge bg="warning" text="dark">Not submitted</Badge>}
                    </div>
                  )}
                  {order.hasSands && (
                    <div>
                      S&amp;S:{" "}
                      {order.sentToSandsDate
                        ? formatDate(order.sentToSandsDate)
                        : <Badge bg="warning" text="dark">Not submitted</Badge>}
                    </div>
                  )}
                  {order.hasPrint && (order.hasSanmar || order.hasSands) && (
                    <div><em>Also includes print items</em></div>
                  )}
                  {!order.hasPrint && !order.hasSanmar && !order.hasSands && (
                    <em>No SanMar / S&amp;S / Print items</em>
                  )}
                </>
              )}
            </Col>
            <Col xs={2} className="text-nowrap">
              <Button
                size="sm"
                onClick={() => navigate("/admin/closedpo/" + order.id, { state: { poId: order.id }, replace: true })}
              >
                View Details
              </Button>
            </Col>
          </Row>
        ))}
      </Container>

      {!loading && orders.length === 0 && (
        <div className="mt-3">No Orders</div>
      )}
    </>
  );
};
export default AdminClosedPOList;
